// Stocky CSV／手作り CSV 取り込みの仕様。UI は CsvImportPanel が共通で持つので、
// ここには「どの列を対応付けるか・どのエンドポイントか・件数をどう言うか」だけ。
//
// 🔴 **画面に出しているのは REORDER_POLICY_SPEC と棚卸しの2つだけ**
// （2026-07-31 ユーザー判断＝画面に出す CSV はこの2つで、仕入先と
// 商品↔仕入先は API キーが使えるあいだ「Stocky データのコピー」が取る）。
// SUPPLIER_SPEC / SUPPLIER_LINK_SPEC は**残してある**＝8/31 以降に来た店から
// 要望が出たら StockyCsvImport/index.tsx に CsvImportPanel を2行戻すだけ。
// 並び順に意味がある＝紐付けは仕入先名で仕入先を引くため、仕入先を先に入れる
// （先に紐付けを流しても仕入先は自動作成されるが、会社名・担当者・連絡先は
// 空のままになる）。
//
// 🔴 表示言語（英語UI）の扱いが2種類ある:
//   - 固定の文字列（title / description / fields.label / readyHint /
//     overwriteNoun）は【日本語のまま置く＝これが EN 辞書のキー】。ここは
//     モジュール読込時に評価される定数で、locale 確定（main.tsx の initLocale）
//     より先に走るため、この場所で t() を呼ぶと訳が ja で焼き付く。訳は描画時＝
//     CsvImportPanel 側で t() を通す。
//   - 件数を含む文（summarize / resultTitle）は関数＝描画時に評価されるので
//     ここで t() してよい。原文テンプレート（`{name}` 入り）を辞書キーにする。
import {
  applyReorderPolicyCsv,
  applyStocktakeHistoryCsv,
  applySupplierCsv,
  applySupplierLinkCsv,
  previewReorderPolicyCsv,
  previewStocktakeHistoryCsv,
  previewSupplierCsv,
  previewSupplierLinkCsv,
  type ReorderPolicyCsvResult,
  type StocktakeHistoryCsvResult,
  type StockyStocktakeListMetadataInput,
  type SupplierCsvResult,
  type SupplierLinkCsvResult,
} from "../../api/stocky_csv_import";
import type { StockyPublicBigIntId } from "../../api/stocky_public_ids";
import type { CsvImportSpec } from "../../components/csvImportSpec";
import { t } from "../../lib/i18n";

export const SUPPLIER_SPEC: CsvImportSpec<SupplierCsvResult> = {
  id: "csv-suppliers",
  title: "仕入先",
  description:
    "Stocky の仕入先（Suppliers）エクスポートを取り込みます。同じ仕入先名が既にあれば上書きし、無ければ新規に登録します。3つのうち、これを最初に実行してください。",
  fields: [
    { key: "name", label: "仕入先名の列（必須）", isRequired: true },
    { key: "company_name", label: "会社名の列", isRequired: false },
    { key: "contact_name", label: "担当者名の列", isRequired: false },
    { key: "contact_email", label: "メールアドレスの列", isRequired: false },
    { key: "phone", label: "電話番号の列", isRequired: false },
  ],
  readyHint: "仕入先名の列を選ぶと取り込めます。",
  preview: previewSupplierCsv,
  apply: applySupplierCsv,
  overwriteNoun: "仕入先",
  overwriteCount: (result) => result.updated,
  writtenCount: (result) => result.created + result.updated,
  summarize: (result) =>
    t("新規に登録 {created} 件・既存を上書き {updated} 件", {
      created: result.created,
      updated: result.updated,
    }),
  resultTitle: (result) =>
    t("仕入先を新規 {created} 件・上書き {updated} 件 取り込みました", {
      created: result.created,
      updated: result.updated,
    }),
  unmatchedSkus: () => [],
};

export const SUPPLIER_LINK_SPEC: CsvImportSpec<SupplierLinkCsvResult> = {
  id: "csv-supplier-links",
  title: "商品と仕入先の紐付け",
  description:
    "SKU と仕入先名の対応を取り込み、商品に仕入先を割り当てます。手元に無い仕入先名は自動で新規作成されます（作成した件数は結果に出ます）。仕入単価・入り数・MOQ・リードタイムの列も対応付けると、仕入先ごとの条件として一緒に入ります。",
  fields: [
    { key: "sku", label: "SKU の列（必須）", isRequired: true },
    { key: "supplier_name", label: "仕入先名の列（必須）", isRequired: true },
    { key: "unit_cost", label: "仕入単価の列", isRequired: false },
    {
      key: "pack_size",
      label: "入り数（1ケースの個数）の列",
      isRequired: false,
    },
    {
      key: "min_order_qty",
      label: "最低発注数量（MOQ）の列",
      isRequired: false,
    },
    {
      key: "lead_time_days",
      label: "リードタイム（日数）の列",
      isRequired: false,
    },
  ],
  readyHint: "SKU の列と仕入先名の列を選ぶと取り込めます。",
  preview: previewSupplierLinkCsv,
  apply: applySupplierLinkCsv,
  overwriteNoun: "仕入条件",
  overwriteCount: (result) => result.conditions_written,
  writtenCount: (result) =>
    result.linked_variants +
    result.suppliers_created +
    result.conditions_written,
  summarize: (result) =>
    t(
      "仕入先を紐付ける商品 {linked} 件・自動で作成する仕入先 {created} 件・書き込む仕入条件 {conditions} 件",
      {
        linked: result.linked_variants,
        created: result.suppliers_created,
        conditions: result.conditions_written,
      },
    ),
  resultTitle: (result) =>
    t(
      "{linked} 件の商品に仕入先を紐付けました（自動で作成した仕入先 {created} 件）",
      { linked: result.linked_variants, created: result.suppliers_created },
    ),
  unmatchedSkus: (result) => result.unmatched_skus,
};

export const REORDER_POLICY_SPEC: CsvImportSpec<ReorderPolicyCsvResult> = {
  id: "csv-reorder-policies",
  title: "Low Stock・発注点／リードタイム",
  description:
    "Stocky の Low Stock CSV から、商品ごとの発注点（ROP）と Vendor ごとのリードタイムを取り込みます。リードタイムは仕入先別の条件として保存し、他の仕入条件は変更しません。最低発注数量（MOQ）は下の専用貼り付け欄から取り込んでください。",
  // 2026-07-31 に実ストアで確認した経路。列名まで書くのは、この画面の CSV が
  // 14列あり、そのうち使うのが4列だけだから（どれを選ぶかで必ず迷う）。
  exportPath:
    "Stocky で Reports → Low Stock → SKU/Variants → CSV。使う列は SKU・Vendor・ROP（発注点）・Lead Time の4つです。",
  expectedFileName: "low_stock_csv.csv",
  autoMapKnownColumns: true,
  fields: [
    { key: "sku", label: "SKU の列（必須）", isRequired: true },
    {
      key: "supplier_name",
      label: "仕入先（Vendor）の列",
      isRequired: false,
    },
    { key: "reorder_point", label: "発注点の列", isRequired: false },
    {
      key: "lead_time_days",
      label: "リードタイム（日数）の列",
      isRequired: false,
    },
  ],
  // 値の列をどれも選ばない場合と、Lead Time を商品全体へ誤って適用し得る
  // Vendor 未選択を、送信前にも止める。サーバ側も同じ条件を 422 にする。
  isReady: (isSelected) =>
    isSelected("sku") &&
    (isSelected("reorder_point") || isSelected("lead_time_days")) &&
    (!isSelected("lead_time_days") || isSelected("supplier_name")),
  readyHint:
    "SKU の列と、発注点またはリードタイムの列を選んでください。リードタイムを選ぶ場合は仕入先（Vendor）の列も必要です。",
  preview: previewReorderPolicyCsv,
  apply: applyReorderPolicyCsv,
  overwriteNoun: "設定",
  overwriteCount: (result) =>
    result.updated + result.lead_times_updated + result.preferred_repaired,
  writtenCount: (result) =>
    result.created +
    result.updated +
    result.lead_times_created +
    result.lead_times_updated +
    result.preferred_repaired,
  summarize: (result) =>
    `${t(
      "発注点: 新規 {created} 件・上書き {updated} 件 / 仕入先別リードタイム: 新規 {leadCreated} 件・上書き {leadUpdated} 件（一致した商品 {matched} 件）",
      {
        created: result.created,
        updated: result.updated,
        leadCreated: result.lead_times_created,
        leadUpdated: result.lead_times_updated,
        matched: result.matched_variants,
      },
    )} / ${t(
      "変更なし: 発注点 {unchanged} 件・リードタイム {leadUnchanged} 件 / 既定仕入先の整合修復 {preferredRepaired} 件",
      {
        unchanged: result.unchanged,
        leadUnchanged: result.lead_times_unchanged,
        preferredRepaired: result.preferred_repaired,
      },
    )}`,
  resultTitle: (result) =>
    t(
      "発注点を新規 {created} 件・更新 {updated} 件、仕入先別リードタイムを新規 {leadCreated} 件・更新 {leadUpdated} 件取り込みました",
      {
        created: result.created,
        updated: result.updated,
        leadCreated: result.lead_times_created,
        leadUpdated: result.lead_times_updated,
      },
    ),
  unmatchedSkus: (result) => result.unmatched_skus,
  diagnostics: (result) => [
    {
      count: result.ambiguous_sku_count,
      items: result.ambiguous_skus,
      confirmTemplate:
        "同じ SKU の商品が複数あるため取り込まない行: {count} 件。",
      resultTemplate:
        "同じ SKU の商品が複数あり特定できなかった行（{count} 件）: {values}",
    },
    {
      count: result.unmatched_supplier_count,
      items: result.unmatched_suppliers,
      confirmTemplate:
        "一致する仕入先が見つからない Vendor: {count} 件（この行は取り込まれません）。",
      resultTemplate:
        "一致する仕入先が見つからなかった Vendor（{count} 件）: {values}",
    },
    {
      count: result.ambiguous_supplier_count,
      items: result.ambiguous_suppliers,
      confirmTemplate:
        "同じ名前の仕入先が複数ある Vendor: {count} 件（この行は取り込まれません）。",
      resultTemplate:
        "同じ名前の仕入先が複数あり特定できなかった Vendor（{count} 件）: {values}",
    },
  ],
};

/**
 * 棚卸しの履歴。**実施日を画面で入力させる**ので定数ではなく関数で作る
 * （CSV に日付列が無い＝Stocky の書き出しに入っていない）。
 *
 * 🔴 在庫は動かない。Stocky のCSVは棚卸し途中にも取得でき、
 * Stocky 側の適用済み/未適用はCSVから判定できない。誤調整を避けるため
 * UreyukiBox は記録だけを保存し、現在在庫を変更しない。
 */
export function buildStocktakeHistorySpec(options: {
  countedOn: string | null;
  locationId: StockyPublicBigIntId | null;
  note: string;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
  sourceQuantityInterpretation: "unknown" | "counted" | "expected" | null;
  stockyListMetadata: StockyStocktakeListMetadataInput | null;
}): CsvImportSpec<StocktakeHistoryCsvResult> {
  return {
    id: "csv-stocktakes",
    title: "棚卸し履歴・明細",
    description:
      "Stocky の棚卸し（Stocktakes）を記録として取り込みます。Stocky側で在庫へ適用済みかどうかはCSVから判定できないため、UreyukiBoxは現在在庫を変更せず、源泉値と記録だけを保存します。Stocky は棚卸し1回につき1つの CSV を書き出すので、複数の棚卸しをまとめて選べます。実施日と拠点は分かる場合だけ指定し、不明のままでも後から設定できます。",
    // ファイル名は案内だけ。自動判定はヘッダ署名なので download_csv (1).csv も通る。
    exportPath:
      "Stocky で Inventory → Stocktakes → 残したい棚卸しを1件開く → CSV。棚卸し1回につき1ファイルで、download_csv.csv（download_csv (1).csv / (2).csv も同じ形式）です。",
    fields: [
      {
        key: "shopify_variant_id",
        label: "Shopify のバリアントIDの列（Shopify ID）",
        isRequired: false,
      },
      { key: "sku", label: "SKU の列", isRequired: false },
      {
        key: "product_title",
        label: "商品名の列（Product・削除済み商品の記録用）",
        isRequired: false,
      },
      {
        key: "variant_title",
        label: "バリエーション名の列（Variant・削除済み商品の記録用）",
        isRequired: false,
      },
      {
        key: "difference",
        label: "差異（増減）の列（Adjustment）",
        isRequired: false,
      },
      {
        key: "counted_qty",
        label: "実数＝棚卸し後の在庫の列（Current Stock）",
        isRequired: false,
      },
      {
        key: "expected_qty",
        label: "システム在庫＝棚卸し前の在庫の列",
        isRequired: false,
      },
    ],
    // キー列は2択のどちらか、数量列は3つのうち2つ以上（残りはサーバが導出）。
    // 🔴 差異だけでは絶対値が決まらない＝ここを緩めると 0 を捏造することになる。
    isReady: (isSelected) => {
      const hasKey = isSelected("shopify_variant_id") || isSelected("sku");
      const quantities = ["difference", "counted_qty", "expected_qty"].filter(
        isSelected,
      ).length;
      return hasKey && quantities >= 2;
    },
    readyHint:
      "未知形式では、商品を特定する列（Shopify のバリアントID または SKU）と数量の列を2つ明示してください。列の意味は自動では推測しません。",
    preview: previewStocktakeHistoryCsv,
    apply: (fileBase64, mapping, applyOptions) =>
      applyStocktakeHistoryCsv(fileBase64, mapping, applyOptions, {
        countedOn: options.countedOn,
        locationId: options.locationId,
        note: options.note.trim() || null,
        sourceImportId: options.sourceImportId,
        sourceFileName: options.sourceFileName,
        sourceMediaType: options.sourceMediaType,
        sourceQuantityInterpretation: options.sourceQuantityInterpretation,
        ...(options.stockyListMetadata === null
          ? {}
          : { stockyListMetadata: options.stockyListMetadata }),
      }),
    overwriteNoun: "棚卸し",
    // 既存を書き換えることが無い（毎回新しい履歴を1件作る）＝上書き警告は出さない。
    overwriteCount: () => 0,
    writtenCount: (result) => result.recorded_items,
    summarize: (result) =>
      result.already_imported
        ? t("この CSV は棚卸し #{id} として取り込み済みです", {
            id: result.stocktake_id ?? "-",
          })
        : t(
            "記録 {recorded} 件・現行商品一致 {matched} 件・原本IDのみ {sourceOnly} 件・原文数量が片方のみ {sourceIncomplete} 件・数量なし {skipped} 件・商品未一致 {unmatched} 件・無効 {invalid} 件",
            {
              recorded: result.recorded_items,
              matched: result.matched_current_items,
              sourceOnly: result.source_only_items,
              sourceIncomplete: result.source_incomplete_items,
              skipped: result.skipped_rows,
              unmatched: result.unmatched_count ?? result.unmatched_keys.length,
              invalid: result.invalid_row_count ?? result.invalid_rows.length,
            },
          ),
    resultTitle: (result) =>
      result.already_imported
        ? t("この CSV は取り込み済みです（重複して作成していません）")
        : result.recorded_items > 0
          ? t(
              "棚卸しの履歴を {recorded} 件の明細で記録しました（在庫は変わっていません）",
              { recorded: result.recorded_items },
            )
          : t("記録できる明細が0件のため、棚卸しは作成していません"),
    unmatchedSkus: (result) => result.unmatched_keys,
  };
}
